import React from 'react';

import {OPPOSITE_SIDE, Side} from 'constants/MolbioConstants';
import {BulkAssemblyFragmentsStructuredTableRowMetadata} from 'molbio/bulk-assembly/tables/BulkAssemblyFragmentsStructuredTableAdapter';
import {getValueForOrientation} from 'molbio/bulk-assembly/util/FragmentEndpointUtil';
import OptionCellDropdown, {
  OptionCellDropdownProps,
} from 'notebook/plain-tables/selectors/OptionCellDropdown';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';

interface BulkAssemblyFragmentEndpointProps {
  typedValue: TypedValue;
  onUpdate: (typedValue: TypedValue) => void;
  rowMetadata: BulkAssemblyFragmentsStructuredTableRowMetadata | null;
  locked: boolean;
  side: Side;
}

/**
 * Cell selector component for a bulk assembly endpoint.
 */
export default function BulkAssemblyFragmentEndpoint({
  typedValue,
  onUpdate,
  rowMetadata,
  locked,
  side,
}: BulkAssemblyFragmentEndpointProps): JSX.Element {
  const staticDisplay = formatTypedValue(typedValue);
  const enzymeCutsiteData =
    side === 'start'
      ? (rowMetadata as BulkAssemblyFragmentsStructuredTableRowMetadata)?.enzymeCutsiteData5
      : (rowMetadata as BulkAssemblyFragmentsStructuredTableRowMetadata)?.enzymeCutsiteData3;
  if (locked || enzymeCutsiteData == null) {
    // Similar to BulkAssemblyBinSelector, we want to hide the dropdown entirely when the cell is locked,
    // so avoid using OptionCellDropdown in the locked case.
    return <span>{staticDisplay}</span>;
  } else {
    const converter = getValueForOrientation(
      enzymeCutsiteData.orientation,
      enzymeCutsiteData.modelToDisplayConverters[side],
      enzymeCutsiteData.modelToDisplayConverters[OPPOSITE_SIDE[side]]
    );
    let dropdownOptions: OptionCellDropdownProps['options'];
    if (enzymeCutsiteData.cutsites.length === 0) {
      dropdownOptions = [
        {node: <span className="menu-option is-disabled">No cut sites detected</span>, onUpdateKey: ''},
      ];
    } else {
      dropdownOptions = enzymeCutsiteData.cutsites.map((enzymeCutsite) => {
        const formattedCutsite = converter(enzymeCutsite).toString();
        return {
          node: <span>{formattedCutsite}</span>,
          onUpdateKey: formattedCutsite,
        };
      });
    }
    return (
      <OptionCellDropdown
        locked={false}
        isSelected={true}
        options={dropdownOptions}
        onUpdate={(enzymeCutsite) => {
          onUpdate({type: BaseTypedValueType.STRING, rawValue: enzymeCutsite});
        }}
        lightHoverBackground={true}
        staticDisplay={staticDisplay}
      />
    );
  }
}
